import React, { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import ReactTooltip from 'react-tooltip';
import Loader from 'src/components/Loader';
import { PieChart } from 'react-minimal-pie-chart';
import { makeJobUrl } from 'src/routes/routes';
import { useData } from './hooks';
import s from '../Dashboards.styles';

export const CapManagerDashboardWidget = () => {
  const { jobs, jobsLoading } = useData();
  const { t } = useTranslation();

  const JobStatusComponent = ({ job: { status } }) => {
    if (status === 'under_cap') {
      return (
        <s.JobStatusSpan underCap={true}>
          <Trans>CAP_MANAGER_DASHBOARD_UNDER_CAP</Trans>
        </s.JobStatusSpan>
      );
    }
    if (status === 'ready_for_cap') {
      return (
        <s.JobStatusSpan readyForCap={true}>
          <Trans>CAP_MANAGER_DASHBOARD_READY_FOR_CAP</Trans>
        </s.JobStatusSpan>
      );
    }
    return null;
  };


  const StatusButton = props => {
    const { type: statusButtonValue, count = 0, ...rest } = props;
    const name = statusButtonValue.toLowerCase();

    const colorStyle = {};
    colorStyle[name] = true;

    return (
      <Fragment {...rest}>
        <s.StatusSpan {...colorStyle}>
          <s.StatusSvg key={name} name={`cap-${name}`} />
          {count}
        </s.StatusSpan>
      </Fragment>
    );
  };



  const pieChart = job => {
    const pendingFindingCount = job.openFindingsCount;
    const allFindingCount = job.totalFindingsWithoutNotRequiredCount;

    return (
      <s.Status flex>
        <s.PieChartDiv data-for="CapManagerDashboardWidget" data-tip={t('CAP_MANAGER_DASHBOARD_PENDING_FINDINGS')}>
          <PieChart
            lineWidth={15}
            data={[
              {
                color: '#DC143C',
                value: allFindingCount === 0 ? 0 : pendingFindingCount,
              },
              {
                color: '#d9d9d9',
                value: allFindingCount === 0 ? 1 : allFindingCount - pendingFindingCount,
              },
            ]}
            radius={50}
            animationEasing="ease-out"
            startAngle={-90}
            animate={false}
            animationDuration={500}
            label={() => null}
            viewBoxSize={[100, 100]}
            style={{
              height: '25px',
              width: '25px',
            }}
            segmentsStyle={{
              transition: 'stroke .3s ease',
            }}
            lengthAngle={360}
            segmentsShift={0}
          />
          <s.PieChartText>
            {allFindingCount === 0 ? 0 : `${pendingFindingCount}/${allFindingCount}`}
          </s.PieChartText>
        </s.PieChartDiv>
      </s.Status>
    );
  };

  return (
    <Fragment>
      <s.DashboardComponent>
        <s.DashboardComponentTitle widget>
          <Trans>CAP_MANAGER_DASHBOARD_JOBS_WITH_ACTIVE_CAP</Trans>
        </s.DashboardComponentTitle>
        {jobsLoading ? (
          <Loader />
        ) : (
          <s.WidgetDiv>
            <ReactTooltip id="CapManagerDashboardWidget" place="bottom"/>
            {jobs.map(job => {
              const responsiblePersons = job.findings
                .map(f => f.responsiblePerson)
                .filter(c => !!c);
              const responsiblePersonsText =
                responsiblePersons.length > 0
                  ? responsiblePersons.join(', ')
                  : 'N/A';

              return (
                <s.WidgetCard key={job.jobId}>
                  <s.WidgetCardInnerFindingsDiv>
                    {pieChart(job)}
                    <s.JobName to={makeJobUrl(job.jobId)}>
                      {job.reportNumber}
                    </s.JobName>
                    <s.FactoryName>{job.factoryName}</s.FactoryName>
                    <JobStatusComponent job={job} />
                    {job.actionRequired && (
                      <s.JobStatusSpan actionRequired={true}>
                        <Trans>AUDITOR_DASHBOARD_ACTION_REQUIRED</Trans>
                      </s.JobStatusSpan>
                    )}
                    <s.CardInfoWithMargin>
                      <s.ServicesName
                        data-for="CapManagerDashboardWidget"
                        data-tip={
                          job.serviceNames && job.serviceNames.length > 0
                            ? job.serviceNames.join(', ')
                            : 'N/A'
                        }
                      >
                        <s.ServiceButtonBookIcon name="serviceIconBlue" />
                        {job.serviceNames && job.serviceNames.length > 0
                          ? job.serviceNames.join(', ')
                          : 'N/A'}
                      </s.ServicesName>
                    </s.CardInfoWithMargin>
                    <s.DateAndAssignedTo>
                      <s.DateAndAssignedToLeft>
                        <s.CardSmallTitle>
                          <Trans>AUDITOR_DASHBOARD_AUDIT_START_END_DATE</Trans>
                        </s.CardSmallTitle>
                        <s.CardSmallValue
                          data-for="CapManagerDashboardWidget"
                          data-tip={`${toSimpleUtcDateString(
                            job.auditStartAt,
                          )} / ${toSimpleUtcDateString(job.auditEndAt)}`}
                        >
                          <s.AuditStartDate>
                            {toSimpleUtcDateString(job.auditStartAt)}
                          </s.AuditStartDate>{' '}
                          / {toSimpleUtcDateString(job.auditEndAt)}
                        </s.CardSmallValue>
                      </s.DateAndAssignedToLeft>
                      <s.DateAndAssignedToRight>
                        <s.CardSmallTitle>
                          <Trans>AUDITOR_DASHBOARD_ASSIGNED_TO</Trans>
                        </s.CardSmallTitle>
                        <s.CardSmallValue
                          data-for="CapManagerDashboardWidget"
                          data-tip={
                            job.auditorNames.length === 0
                              ? 'N/A'
                              : job.auditorNames.join(', ')
                          }
                        >
                          {job.auditorNames.length === 0
                            ? 'N/A'
                            : job.auditorNames.join(', ')}
                        </s.CardSmallValue>
                      </s.DateAndAssignedToRight>
                    </s.DateAndAssignedTo>
                  </s.WidgetCardInnerFindingsDiv>
                  <s.BoxLine />
                  <s.WidgetCardInnerFindingsDiv>
                    <s.FindingsDiv>
                      <s.FindingStatusTitleText>
                        <Trans>CAP_MANAGER_DASHBOARD_FINDINGS_STATUS</Trans>
                      </s.FindingStatusTitleText>

                      {job.findings.map((finding, index) => {
                        const {
                          pendingActionCount,
                          approvedActionCount,
                          rejectedActionCount,
                        } = finding;

                        return (
                          <s.FindingDiv key={`findings-${index}`}>
                            <s.BlockWithText data-for="CapManagerDashboardWidget" data-tip={finding?.info?.name}>
                              {finding?.info?.name}
                            </s.BlockWithText>
                            {pendingActionCount !== 0 && (
                              <StatusButton
                                count={pendingActionCount}
                                type="Pending"
                              />
                            )}
                            {approvedActionCount !== 0 && (
                              <StatusButton
                                count={approvedActionCount}
                                type="Approved"
                              />
                            )}
                            {rejectedActionCount !== 0 && (
                              <StatusButton
                                count={rejectedActionCount}
                                type="Rejected"
                              />
                            )}
                            {pendingActionCount +
                              approvedActionCount +
                              rejectedActionCount ===
                              0 && (
                              <s.NoActionSpan>
                                <Trans>CAP_MANAGER_DASHBOARD_NO_ACTIONS</Trans>
                              </s.NoActionSpan>
                            )}
                          </s.FindingDiv>
                        );
                      })}
                    </s.FindingsDiv>
                    <s.ResponsiblePersonTitle>
                      <Trans>CAP_MANAGER_DASHBOARD_RESPONSIBLE_PERSON</Trans>
                    </s.ResponsiblePersonTitle>
                    <s.ResponsiblePersonText data-for="CapManagerDashboardWidget" data-tip={responsiblePersonsText}>
                      {responsiblePersonsText}
                    </s.ResponsiblePersonText>
                  </s.WidgetCardInnerFindingsDiv>
                </s.WidgetCard>
              );
            })}
          </s.WidgetDiv>
        )}
      </s.DashboardComponent>
    </Fragment>
  );
};

export default React.memo(CapManagerDashboardWidget);
